import PropTypes from "prop-types";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { Grid, Icon, Pagination, Popup, Segment, Table } from "semantic-ui-react";
import { summarizeList } from "../../../libs/common_utils";
import PixelGridHeader from "../../pixel/components/PixelGridHeader";
import { useRetargetingSegmentsContext } from "../index/context";
import GridEmptyRow from "./GridEmptyRow";
import RetargetingSegmentsFilterControl from "./RetargetingSegmentsFilterControl";

const RetargetingSegmentGrid = ({ items, loading }) => {
  const { state, getPage, pager } = useRetargetingSegmentsContext();
  const hasFilter = state.filter.title !== "";
  const filteredData = hasFilter
    ? items.filter((item) => item.name.toLowerCase().includes(state.filter.title.toLowerCase()))
    : items;
  const intl = useIntl();
  const tableHeaders = useMemo(
    () => [
      {
        title: intl.formatMessage({
          id: "LABEL_BRAND_SKU_SEGMENT",
          defaultMessage: "Brand and SKU Segment",
        }),
        textAlign: "left",
      },
      {
        title: intl.formatMessage({
          id: "LABEL_BRAND_SKU_SEGMENT_ID",
          defaultMessage: "Brand and SKU Segment ID",
        }),
        textAlign: "left",
      },
      {
        title: intl.formatMessage({
          id: "LABEL_ADVERTISER_NAME",
          defaultMessage: "Advertiser Name",
        }),
        textAlign: "left",
      },
      {
        title: intl.formatMessage({
          id: "LABEL_ADVERTISER_ID",
          defaultMessage: "Advertiser ID",
        }),
        textAlign: "left",
      },
      {
        title: intl.formatMessage({
          id: "LABEL_EVENT_TYPE",
          defaultMessage: "Event Type",
        }),
        textAlign: "left",
      },
      {
        title: intl.formatMessage({
          id: "LABEL_BRAND_NAMES",
          defaultMessage: "Brand Names",
        }),
        textAlign: "left",
      },
      {
        title: intl.formatMessage({
          id: "LABEL_SKUS",
          defaultMessage: "SKUs",
        }),
        textAlign: "left",
      },
      {
        title: "",
        textAlign: "center",
        style: { width: "70px" },
      },
    ],
    [intl],
  );

  return (
    <>
      <PixelGridHeader />
      <Segment
        basic
        loading={loading}
      >
        <Grid className="common_grid">
          <Grid.Row columns={16}>
            <RetargetingSegmentsFilterControl />
          </Grid.Row>
          <Table
            className="custom-table"
            style={{ padding: "0" }}
          >
            <Table.Header>
              <Table.Row>
                {tableHeaders.map((item) => (
                  <Table.HeaderCell
                    key={item.title}
                    className={item?.className}
                    textAlign={item?.textAlign}
                    style={item?.style}
                  >
                    {item.title}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {filteredData.length === 0 && !loading && (
                <GridEmptyRow
                  hasFilter={hasFilter}
                  colSpan={tableHeaders.length}
                />
              )}
              {filteredData.map((item) => (
                <Table.Row key={item.id}>
                  <Table.Cell>{item.name}</Table.Cell>
                  <Table.Cell>{item.t1_segment_id}</Table.Cell>
                  <Table.Cell>{item.advertiser_name}</Table.Cell>
                  <Table.Cell>{item.advertiser_id}</Table.Cell>
                  <Table.Cell>{item.event_type?.toUpperCase() || "-"}</Table.Cell>
                  <Table.Cell>
                    {item?.brand_names?.length > 0 ? (
                      <Popup
                        size="tiny"
                        content={item.brand_names.join(", ")}
                        inverted
                        trigger={<span>{summarizeList(item.brand_names)}</span>}
                      />
                    ) : (
                      "-"
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {item?.sku_ids?.length > 0 ? (
                      <Popup
                        size="tiny"
                        content={item.sku_ids.join(", ")}
                        inverted
                        trigger={<span>{summarizeList(item.sku_ids)}</span>}
                      />
                    ) : (
                      "-"
                    )}
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Link to={`/retargeting-segments/${item.id}/edit`}>
                      <Icon name="edit" />
                    </Link>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
            <Table.Footer>
              <Table.Row>
                <Table.Cell
                  colSpan={tableHeaders.length}
                  textAlign="right"
                >
                  <Pagination
                    size="mini"
                    activePage={pager.page}
                    totalPages={pager.total_pages}
                    firstItem={null}
                    lastItem={null}
                    onPageChange={getPage}
                  />
                </Table.Cell>
              </Table.Row>
            </Table.Footer>
          </Table>
        </Grid>
      </Segment>
    </>
  );
};

export default RetargetingSegmentGrid;

RetargetingSegmentGrid.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
};
